import { useState, type ReactNode } from 'react';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';
import {
  useFundPortalPublication,
  useUpdateFundPortalPublication,
} from '@/hooks/react-query/useFunds';
import { cn } from '@/lib/utils';
import type {
  Fund,
  FundPortalPublication,
  FundPortalPublicationUpdate,
  MetricsCadence,
} from '@/types/fund';
import { METRICS_CADENCE_LABELS } from '@/types/fund';
import { formatDateTime } from '@/utils/formatters';

interface FundPortalVisibilityTabProps {
  fund: Fund;
}

type CategoryKey = keyof Pick<
  FundPortalPublicationUpdate,
  | 'publish_fund_details'
  | 'publish_holdings'
  | 'publish_portfolio_companies'
  | 'publish_transactions'
  | 'publish_dashboard'
>;

const CATEGORIES: { key: CategoryKey; label: string; description: string }[] = [
  {
    key: 'publish_fund_details',
    label: 'Fund details',
    description: 'Fund overview, structure and key facts.',
  },
  {
    key: 'publish_holdings',
    label: 'Holdings',
    description: 'Commitments, capital called and NAV.',
  },
  {
    key: 'publish_portfolio_companies',
    label: 'Portfolio companies',
    description: 'Underlying portfolio company information.',
  },
  {
    key: 'publish_transactions',
    label: 'Transactions',
    description: 'Drawdowns and distributions, including recent activity.',
  },
  {
    key: 'publish_dashboard',
    label: 'Dashboard',
    description:
      'Overview metrics and recent activity on the portal home page.',
  },
];

const CADENCES = Object.keys(METRICS_CADENCE_LABELS) as MetricsCadence[];

function toFormState(
  publication: FundPortalPublication
): FundPortalPublicationUpdate {
  return {
    is_published: publication.is_published,
    published_as_of_date: publication.published_as_of_date,
    publish_fund_details: publication.publish_fund_details,
    publish_holdings: publication.publish_holdings,
    publish_portfolio_companies: publication.publish_portfolio_companies,
    publish_transactions: publication.publish_transactions,
    publish_dashboard: publication.publish_dashboard,
    metrics_cadence: publication.metrics_cadence,
    metrics_auto_release: publication.metrics_auto_release,
    geena_enabled: publication.geena_enabled,
  };
}

/**
 * Everything below the master switch, revealed by it.
 *
 * A grid that animates between `1fr` and `0fr` collapses to its own content
 * height without anyone measuring it, and the inner wrapper's `overflow:hidden`
 * is what actually clips during the transition.
 */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      data-open={open}
      aria-hidden={!open}
      inert={!open}
      className={cn(
        'grid transition-[grid-template-rows,opacity,margin-top] duration-300 ease-out motion-reduce:transition-none',
        open ? 'mt-1 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * One of the two mutually exclusive release modes. The active one is filled in
 * and carries the badge; the inactive one dims and stops taking input, so the
 * pair reads as a single choice rather than two independent settings.
 */
function ReleaseMode({
  title,
  description,
  active,
  control,
  children,
}: {
  title: string;
  description: string;
  active: boolean;
  control?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'space-y-1 rounded-xl border p-4 transition-colors',
        active
          ? 'border-muted-foreground/50 bg-muted/75'
          : 'border-border/55 bg-transparent'
      )}
    >
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-0.5 pr-4">
          <div className="flex items-center gap-2">
            <h5 className="text-sm font-medium">{title}</h5>
            {active && (
              <Badge
                variant="secondary"
                className="border-border bg-accent text-accent-foreground"
              >
                Active
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {control}
      </div>
      <div
        className={cn(
          'mt-3 border-t border-border/75 pt-3 transition-opacity duration-200',
          active ? 'opacity-100' : 'opacity-45'
        )}
        aria-hidden={!active}
        inert={!active}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Inner form, mounted only once the publication record is available. It seeds
 * its local draft from props via a lazy initializer and is remounted (via the
 * caller's `key`) whenever the persisted record changes, so saved state always
 * becomes the new baseline without effect-driven state syncing.
 */
function PortalVisibilityForm({
  fundId,
  publication,
}: {
  fundId: string;
  publication: FundPortalPublication;
}) {
  const updateMutation = useUpdateFundPortalPublication();
  const [form, setForm] = useState<FundPortalPublicationUpdate>(() =>
    toFormState(publication)
  );

  const baseline = toFormState(publication);
  const isDirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const autoRelease = form.metrics_auto_release;

  const toggle = (key: keyof FundPortalPublicationUpdate) =>
    setForm((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-6">
      {publication.has_unreviewed_updates && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>New portal-eligible data available</AlertTitle>
          <AlertDescription>
            {publication.sync_notes ??
              'New data has become available for this fund since the last review. Review the categories below and publish what should be visible to investors.'}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              Investor Portal Visibility
              {form.is_published ? (
                <Badge variant="success" className="gap-1">
                  <Eye className="h-3 w-3" /> Published
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <EyeOff className="h-3 w-3" /> Hidden
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Control whether this fund and its data are exposed to investors in
              the portal. Nothing is shown until the fund is published.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Master switch — everything below hangs off it. */}
          <div className="flex items-center justify-between gap-6 py-3">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="is_published" className="text-base">
                Fund visible in investor portal
              </Label>
              <p className="text-sm text-muted-foreground">
                Master switch. When off, investors see nothing at all for this
                fund - not its details, portfolio companies or capital
                commitments, and not its drawdowns or notices - whatever the
                settings below say.
              </p>
            </div>
            <Switch
              id="is_published"
              checked={form.is_published}
              onCheckedChange={() => toggle('is_published')}
            />
          </div>

          <Collapse open={form.is_published}>
            <div className="space-y-6">
              <div className="space-y-3 pl-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Data categories</h4>
                  <p className="text-sm text-muted-foreground">
                    Choose which information is exposed once the fund is
                    published.
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {CATEGORIES.map((category) => (
                    <div
                      key={category.key}
                      className="flex items-center justify-between gap-6 py-3"
                    >
                      <div className="space-y-0.5 pr-4">
                        <Label htmlFor={category.key}>{category.label}</Label>
                        <p className="text-sm text-muted-foreground">
                          {category.description}
                        </p>
                      </div>
                      <Switch
                        id={category.key}
                        checked={form[category.key]}
                        disabled={!form.is_published}
                        onCheckedChange={() => toggle(category.key)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-4 pl-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Data release</h4>
                  <p className="text-sm text-muted-foreground">
                    Determine the method on which data is released.
                  </p>
                </div>

                <ReleaseMode
                  title="Automatic release"
                  description="Auto-release on close: finalizing a close publishes the metrics immediately, with no confirmation step."
                  active={autoRelease}
                  control={
                    <Switch
                      id="metrics_auto_release"
                      checked={autoRelease}
                      onCheckedChange={() => toggle('metrics_auto_release')}
                    />
                  }
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="space-y-0.5 pr-4">
                      <Label htmlFor="metrics_cadence">Reporting cadence</Label>
                      <p className="text-sm text-muted-foreground">
                        Period used when releasing fund metrics to investors.
                      </p>
                    </div>
                    <Select
                      value={form.metrics_cadence}
                      disabled={!autoRelease}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          metrics_cadence: value as MetricsCadence,
                        }))
                      }
                    >
                      <SelectTrigger
                        id="metrics_cadence"
                        className="w-44 shrink-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CADENCES.map((cadence) => (
                          <SelectItem key={cadence} value={cadence}>
                            {METRICS_CADENCE_LABELS[cadence]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </ReleaseMode>

                <ReleaseMode
                  title="Manual release"
                  description="Nothing is published automatically. You set the date up to which information is released."
                  active={!autoRelease}
                >
                  <div className="space-y-2">
                    <Label htmlFor="published_as_of_date">
                      Information as of
                    </Label>
                    <div className="max-w-xs">
                      <DateInput
                        id="published_as_of_date"
                        value={form.published_as_of_date ?? ''}
                        disabled={autoRelease}
                        onChange={(value) =>
                          setForm((prev) => ({
                            ...prev,
                            published_as_of_date: value || null,
                          }))
                        }
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Shown to investors as the currency of the information
                      (e.g. &quot;Information as of&quot;).
                    </p>
                  </div>
                </ReleaseMode>
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-6 pl-4">
                <div className="space-y-0.5 pr-4">
                  <Label htmlFor="geena_enabled">Geena</Label>
                  <p className="text-sm text-muted-foreground">
                    Offer Geena to this fund&apos;s investors in the portal.
                  </p>
                </div>
                <Switch
                  id="geena_enabled"
                  checked={form.geena_enabled}
                  onCheckedChange={() => toggle('geena_enabled')}
                />
              </div>

              <Separator />

              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Last sync check</span>
                  <p className="font-medium">
                    {publication.last_sync_checked_at
                      ? formatDateTime(publication.last_sync_checked_at)
                      : 'Not yet checked'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Last published</span>
                  <p className="font-medium">
                    {publication.last_published_at
                      ? formatDateTime(publication.last_published_at)
                      : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </Collapse>

          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setForm(baseline)}
              disabled={!isDirty || updateMutation.isPending}
            >
              Reset
            </Button>
            <Button
              onClick={() => updateMutation.mutate({ fundId, data: form })}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function FundPortalVisibilityTab({
  fund,
}: FundPortalVisibilityTabProps) {
  const { data: publication, isLoading } = useFundPortalPublication(fund.id);

  return (
    <TabsContent value="portal" className="space-y-6 flex-1">
      {isLoading || !publication ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <PortalVisibilityForm
          key={publication.updated_at}
          fundId={fund.id}
          publication={publication}
        />
      )}
    </TabsContent>
  );
}
